import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/dates';

const CHANGED = 'ventas-lockdown-changed';
const announceChange = () => window.dispatchEvent(new Event(CHANGED));

// The alarm. Any admin can pull it; it freezes the system for everyone but owners, ends the other
// sessions and emails the admins. Only an owner can lift it.
export const EmergencyModal = ({ onClose }) => {
  const [reason, setReason] = useState('');
  const [revoke, setRevoke] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const activate = async () => {
    setBusy(true); setError('');
    try {
      await API.post('/admin/lockdown-on', { reason, revokeSessions: revoke });
      announceChange();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo activar el bloqueo.');
    } finally { setBusy(false); }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton style={{ borderBottom: '2px solid var(--mm-red)' }}>
        <Modal.Title style={{ color: 'var(--mm-red)' }}><i className="bi bi-shield-exclamation me-2" />Bloqueo de emergencia</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Congela el sistema <strong>de inmediato</strong>:</p>
        <ul className="small">
          <li>Solo los <strong>propietarios</strong> podrán entrar o usar el sistema; todos los demás quedan fuera.</li>
          <li>Se cierran las sesiones abiertas de los demás usuarios (recomendado, para que ninguna sesión robada siga viva).</li>
          <li>Las solicitudes de aprobación quedan congeladas.</li>
          <li>Se avisa por correo a los administradores.</li>
          <li>Solo un propietario, con su contraseña, puede levantarlo.</li>
        </ul>
        <Form.Label className="d-block small text-muted mb-1">Motivo (opcional; queda en el registro y en el correo)</Form.Label>
        <Form.Control as="textarea" rows={2} maxLength={300} autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="Ej.: movimientos raros en la cuenta de un usuario" />
        <Form.Check className="mt-3" id="revoke-sessions" type="checkbox" checked={revoke} onChange={e => setRevoke(e.target.checked)}
          label="Cerrar las sesiones de todos los demás usuarios (recomendado)" />
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="danger" disabled={busy} onClick={activate}>{busy ? 'Activando...' : 'ACTIVAR BLOQUEO'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

// The red button in the header, for admins.
export const EmergencyButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-outline-danger btn-sm" title="Bloqueo de emergencia" onClick={() => setOpen(true)}><i className="bi bi-shield-exclamation" /></button>
      {open && <EmergencyModal onClose={() => setOpen(false)} />}
    </>
  );
};

// Lifting: an owner, re-confirming their password.
const LiftModal = ({ onClose }) => {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { await API.post('/admin/lockdown-off', { password }); announceChange(); onClose(); }
    catch (err) { setError(err.response?.data?.message || 'No se pudo levantar el bloqueo.'); setPassword(''); }
    finally { setBusy(false); }
  };
  return (
    <Modal show onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton><Modal.Title>Levantar el bloqueo de emergencia</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="small text-muted">El sistema volverá a estar disponible para todos. Las sesiones que se cerraron no se restauran: cada quien tendrá que iniciar sesión de nuevo. Confirma con tu contraseña.</p>
          <Form.Control type="password" autoFocus autoComplete="current-password" placeholder="Tu contraseña" value={password} onChange={e => setPassword(e.target.value)} />
          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="danger" disabled={busy || !password}>{busy ? 'Verificando...' : 'Levantar bloqueo'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// A red bar across every page, for admins, for as long as a lockdown is on.
export const LockdownBanner = () => {
  const { user } = useAuth() || {};
  const [state, setState] = useState(null);
  const [lifting, setLifting] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(() => {
    if (!isAdmin) { setState(null); return; }
    API.get('/admin/lockdown', { silent: true }).then(r => setState(r.data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    window.addEventListener(CHANGED, load);
    return () => { clearInterval(timer); window.removeEventListener(CHANGED, load); };
  }, [load]);

  if (!isAdmin || !state?.active) return null;
  const since = formatDateTime(state.since, '');
  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-2" style={{ background: 'var(--mm-red)', color: '#2b0a0a', fontWeight: 600 }}>
        <div>
          <i className="bi bi-shield-fill-exclamation me-2" />SISTEMA EN BLOQUEO DE EMERGENCIA
          <span style={{ fontWeight: 400 }}> — desde {since}{state.by ? ` por ${state.by}` : ''}{state.reason ? `. Motivo: ${state.reason}` : ''}. Solo los propietarios pueden usar el sistema.</span>
        </div>
        {state.canLift
          ? <Button size="sm" variant="dark" onClick={() => setLifting(true)}>Levantar bloqueo</Button>
          : <span style={{ fontWeight: 400 }}>Solo un propietario puede levantarlo.</span>}
      </div>
      {lifting && <LiftModal onClose={() => setLifting(false)} />}
    </>
  );
};
